import { Pressable } from "react-native";
import { usePathname, useRouter } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import type { SortSelection } from "@src/features/clips/types/camera";
import { useTenant, getTenantDisplayName } from "@src/features/tenants";
import { Logo } from "./Logo.web";
import { SidepanelCameraList } from "./SidepanelCameraList.web";
import { SidepanelActionItem } from "./SidepanelActionItem.web";
import { SidepanelAnimatedActionItem } from "./SidepanelAnimatedActionItem";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { useMemo } from "react";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useCameras } from "@src/features/cameras";
import { SiteDropdown } from "./SiteDropdown.web";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";

interface SidepanelProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  searchQuery: string;
  sortBy: SortSelection;
  onOpenTenantSwitcher?: () => void;
}

export function Sidepanel({ collapsed, onToggleCollapse, searchQuery, sortBy, onOpenTenantSwitcher }: SidepanelProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { theme } = useUnistyles();
  const { selectedTenant } = useTenant();
  const { data: cameras } = useCameras();
  const { contactUrl } = useRegionConfig();
  const tenantName = selectedTenant ? getTenantDisplayName(selectedTenant) : "";
  const selectedSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);

  const siteGroups = useMemo(() => {
    if (!cameras) return [];
    return groupCamerasBySite(cameras, "site");
  }, [cameras]);

  const selectedSiteName = useMemo(() => {
    if (!selectedSiteId) return null;
    return siteGroups.find((g) => g.siteId === selectedSiteId)?.siteName ?? null;
  }, [selectedSiteId, siteGroups]);

  const handleAlarmSettings = () => {
    if (selectedTenant?.id) {
      router.push(`/tenants/${selectedTenant.id}/alarm-settings` as never);
    }
  };

  const handleLogout = () => {
    router.push("/auth/logout");
  };

  const handleHelp = () => {
    // Handled via link semantics when href/externalHref is present
  };

  const handleSettings = () => {
    router.push("/settings");
  };

  const handleLogoPress = () => {
    router.push("/");
  };

  // Collapsed state
  if (collapsed) {
    return (
      <View
        width={150}
        bg="surfaceCard"
        borderRadius={sizes.radius}
        p="6xl"
        gap="xl"
        borderWidth={1}
        borderLeftWidth={0}
        borderColor="borderCard"
        shadow="xl"
      >
        {/* Logo */}
        <View alignItems="center">
          <Pressable onPress={handleLogoPress} accessibilityRole="button">
            <Logo collapsed />
          </Pressable>
        </View>

        {/* Divider */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>

        {/* Expand button */}
        <View alignItems="center">
          <Pressable onPress={onToggleCollapse} style={styles.collapseToggle}>
            <Icon name="chevronRight" size={theme.spacing["5xl"]} color={theme.colors.textMuted} />
          </Pressable>
        </View>

        {/* Camera List — fills remaining space */}
        <View flex={1}>
          <View flexDirection="row" justifyContent="space-between" alignItems="center" py="xl">
            <View flexDirection="row" alignItems="center" gap="xs" justifyContent="center" style={{ width: "100%" }}>
              <Icon name="videoOutline" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
            </View>
          </View>
          <SidepanelCameraList searchQuery={searchQuery} sortBy={sortBy} collapsed selectedSiteId={selectedSiteId} />
        </View>

        <View gap="lg">
          {/* <SidepanelAnimatedActionItem
          label="Activity"
          icon="motion"
          onPress={() => {}}
          isSelected={pathname.endsWith("/activity")}
          collapsed
        /> */}
          <FeatureGate flag={FEATURE_FLAGS.SITE_WIDE_CONTROLS}>
            <SidepanelAnimatedActionItem
              label="Alarm Settings"
              icon="settingsCamera"
              onPress={handleAlarmSettings}
              isSelected={pathname.includes("/alarm-settings") && !pathname.includes("/cameras/")}
              collapsed
            />
          </FeatureGate>
        </View>

        {/* Divider */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>

        {/* Bottom actions */}
        <View gap="lg">
          <SidepanelAnimatedActionItem
            label="Settings"
            icon="settingsOutline"
            onPress={handleSettings}
            href={"/settings"}
            isSelected={pathname.endsWith("/settings")}
            collapsed
          />
          <SidepanelActionItem label="Help" icon="help" onPress={handleHelp} externalHref={contactUrl} collapsed />
          <SidepanelActionItem label="Log out" icon="logout" onPress={handleLogout} collapsed colorToken="danger" />
        </View>
      </View>
    );
  }

  // Expanded state
  return (
    <View
      width={sizes.webSidebarWidth}
      bg="surfaceCard"
      borderRadius={sizes.radius}
      p="6xl"
      gap="xl"
      borderWidth={1}
      borderLeftWidth={0}
      borderColor="borderCard"
      shadow="xl"
    >
      {/* Logo + separator */}
      <View gap="2xl">
        {/* Top row with logo and collapse button */}
        <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" height={36}>
          <Pressable onPress={handleLogoPress} accessibilityRole="button">
            <Logo />
          </Pressable>
          <Pressable onPress={onToggleCollapse} style={styles.collapseToggle}>
            <Icon name="chevronLeft" size={theme.spacing["5xl"]} color={theme.colors.textMuted} />
          </Pressable>
        </View>

        {/* Separator */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>
      </View>

      <SiteDropdown
        siteGroups={siteGroups}
        selectedSiteId={selectedSiteId}
        selectedSiteName={selectedSiteName}
        onSelectSite={setSelectedSiteId}
      />

      {/* Camera List — fills all remaining space */}
      <View flex={1}>
        <View flexDirection="row" justifyContent="space-between" alignItems="center" py="xl">
          <View flexDirection="row" alignItems="center" gap="xs">
            <Icon name="videoOutline" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
            <Text textStyle="sm/regular" color="textSecondary">
              Cameras
            </Text>
          </View>
          <Icon name="chevronRight" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
        </View>
        <SidepanelCameraList searchQuery={searchQuery} sortBy={sortBy} selectedSiteId={selectedSiteId} />
      </View>

      <View gap="lg">
        {/* <SidepanelAnimatedActionItem
          label="Activity"
          icon="motion"
          onPress={() => {}}
          isSelected={pathname.endsWith("/activity")}
        /> */}
        <FeatureGate flag={FEATURE_FLAGS.SITE_WIDE_CONTROLS}>
          <SidepanelAnimatedActionItem
            label="Alarm Settings"
            icon="settingsCamera"
            onPress={handleAlarmSettings}
            isSelected={pathname.includes("/alarm-settings") && !pathname.includes("/cameras/")}
          />
        </FeatureGate>
      </View>

      {/* Bottom Section */}
      <View gap="xl">
        {/* Separator */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>

        {onOpenTenantSwitcher && (
          <Pressable
            onPress={onOpenTenantSwitcher}
            accessibilityRole="button"
            accessibilityLabel="Switch organisation"
            testID="tenant-switcher-trigger"
            style={styles.tenantSwitcher}
          >
            <Icon name="swapVertical" size={theme.spacing["5xl"]} color={theme.colors.icon} />
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                Organisation
              </Text>
              <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                {tenantName}
              </Text>
            </View>
            <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
          </Pressable>
        )}

        {/* Navigation items */}
        <View gap="lg">
          <SidepanelAnimatedActionItem
            label="Settings"
            icon="settingsOutline"
            onPress={handleSettings}
            href={"/settings"}
            isSelected={pathname.endsWith("/settings")}
          />
          <SidepanelActionItem label="Help" icon="help" onPress={handleHelp} externalHref={contactUrl} />
          <SidepanelActionItem label="Log out" icon="logout" onPress={handleLogout} colorToken="danger" />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  collapseToggle: {
    width: 36,
    minWidth: 36,
    height: 36,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
    alignItems: "center",
    justifyContent: "center",
  },
  tenantSwitcher: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
  },
}));
